import ChevronLeftIcon from '../../assets/icons/chevron-left.svg?react';
import { NotificationEvent, NotificationMember } from '../../gql/generated';
import { cn } from '../../utils/className';
import { formatMetricName } from '../../utils/formatMetric';
import {
  formatNotificationDate,
  notificationStateLabel,
  notificationStateVariant,
} from '../../utils/notificationFormatters';
import { Badge } from '../Badge';
import { IconButton } from '../IconButton';
import { NotificationDetailContent } from '../notifications/NotificationDetailContent';

interface AlarmEventDetailProps {
  event: NotificationEvent;
  /** The member alarm the log row stands for. */
  member: NotificationMember;
  onBack: () => void;
}

/** One alarm out of the event log, with the log a step back. */
export function AlarmEventDetail({
  event,
  member,
  onBack,
}: AlarmEventDetailProps) {
  const machineName = event.machineName || 'Unknown machine';
  const title = member.signal
    ? `${machineName} - ${formatMetricName(member.signal)}`
    : machineName;

  return (
    <div className='flex flex-col'>
      <div className='flex items-start gap-3 px-4 py-4 md:px-6'>
        <IconButton
          icon={<ChevronLeftIcon />}
          onClick={onBack}
          aria-label='Back to the event log'
          iconSize='lg'
          // The icon carries a fixed grey fill, so paint it from the text colour
          className='text-focus [&_path]:fill-current'
        />

        <div className='min-w-0 flex-1'>
          <h3 className='size-h4 text-primary truncate font-bold' title={title}>
            {title}
          </h3>
          <p className='size-p2 text-secondary'>
            {formatNotificationDate(event.timeUtc, ' · ')}
          </p>
        </div>

        <Badge variant={notificationStateVariant[event.alarmTriggerState]}>
          {notificationStateLabel[event.alarmTriggerState]}
        </Badge>
      </div>

      {/* Whether the alarm reached its channels. A failed delivery is the one
          way to see from here that nobody was notified. */}
      {(event.notificationDeliveries?.length ?? 0) > 0 && (
        <div className='flex flex-col gap-1 px-4 pb-4 md:px-6'>
          {event.notificationDeliveries!.map((delivery, index) => (
            <p
              key={delivery.notificationChannelId ?? index}
              className={cn(
                'size-p2',
                delivery.success ? 'text-secondary' : 'text-error'
              )}
            >
              {delivery.success ? 'Delivered to' : 'Delivery failed to'}{' '}
              {delivery.notificationChannelName || 'Unknown channel'}
              {!delivery.success && delivery.errorMessage
                ? `: ${delivery.errorMessage}`
                : ''}
            </p>
          ))}
        </div>
      )}

      <NotificationDetailContent
        notification={event}
        member={member}
        className='px-4 pb-6 md:px-6'
      />
    </div>
  );
}
